/**
 * Mirrors App\Services\HR\PayrollCalculator on the API so the employee form
 * preview and the stored payslip always agree on the numbers.
 *
 * A percentage line resolves against one of two bases:
 *  - basic_salary: the employee basic salary alone
 *  - gross_salary: basic salary plus every allowance that does not itself
 *    depend on the gross, which keeps the resolution order acyclic
 */

const round = (amount) => Math.round((Number(amount) || 0) * 100) / 100;

const normalize = (benefit) => ({
  ...benefit,
  name: benefit?.name || '',
  type: benefit?.type === 'deduction' ? 'deduction' : 'allowance',
  calculation_type: benefit?.calculation_type === 'percentage' ? 'percentage' : 'fixed',
  value: Number(benefit?.value) || 0,
  percentage_base: benefit?.percentage_base === 'gross_salary' ? 'gross_salary' : 'basic_salary',
});

const dependsOnGross = (line) =>
  line.calculation_type === 'percentage' && line.percentage_base === 'gross_salary';

const resolveAmount = (line, base) =>
  line.calculation_type === 'percentage' ? round((base * line.value) / 100) : round(line.value);

const sum = (lines) => lines.reduce((total, line) => total + (line.amount || 0), 0);

export const calculateSalary = (basicSalary, benefits = []) => {
  const basic = Number(basicSalary) || 0;
  const lines = (benefits || []).filter(Boolean).map(normalize);

  const baseAllowances = lines
    .filter((line) => line.type === 'allowance')
    .map((line) => (dependsOnGross(line) ? line : { ...line, amount: resolveAmount(line, basic) }));

  const grossBase = basic + sum(baseAllowances);

  const allowances = baseAllowances.map((line) =>
    dependsOnGross(line) ? { ...line, amount: resolveAmount(line, grossBase) } : line
  );

  const totalAllowances = round(sum(allowances));
  const grossSalary = round(basic + totalAllowances);

  const deductions = lines
    .filter((line) => line.type === 'deduction')
    .map((line) => ({
      ...line,
      amount: resolveAmount(line, dependsOnGross(line) ? grossSalary : basic),
    }));

  const totalDeductions = round(sum(deductions));

  return {
    basicSalary: round(basic),
    allowances,
    deductions,
    totalAllowances,
    totalDeductions,
    grossSalary,
    netSalary: round(grossSalary - totalDeductions),
  };
};

/**
 * Human readable summary of how a line is calculated, e.g. "10% of gross salary".
 */
export const describeBenefit = (benefit, t) => {
  if (benefit?.calculation_type === 'percentage') {
    const base =
      benefit?.percentage_base === 'gross_salary'
        ? t('hr.benefits.grossSalary')
        : t('hr.benefits.basicSalary');
    return `${Number(benefit?.value) || 0}% ${t('hr.benefits.of')} ${base}`;
  }

  return t('hr.benefits.fixedAmount');
};
